import * as Clipboard from 'expo-clipboard';
import ChainLinkIcon from '@assets/icons/chain-link-with-borders.svg';
import i18n from '@/utils/i18n';
import React, { useCallback } from 'react';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { EmptyState } from '@/components/empty-state';
import { GetPaymentLinksQuery } from '@/types';
import {
  PaymentLink,
  PaymentLinksList,
} from '@/components/payments/payment-links-list';
import { Platform, RefreshControlProps, Share, View } from 'react-native';
import { Spacing } from '@usepepper/sprout-kit';
import { useToasts } from '@/contexts/toast-context';

interface Props {
  contentSpacing?: Spacing;
  paymentLinks: GetPaymentLinksQuery['getPaymentLinks']['payment_links'];
  refreshControl: React.ReactElement<RefreshControlProps>;
  renderListItem: (paymentLink: PaymentLink) => React.ReactElement | null;
  selectedPaymentLink?: PaymentLink;
}

export const PaymentLinksComponent: React.FC<Props> = ({
  contentSpacing = Spacing.SPACE_MEDIUM,
  paymentLinks,
  refreshControl,
  renderListItem,
  selectedPaymentLink,
}) => {
  const [styles, theme] = useStyles();
  const { showToast } = useToasts();

  const onSharePaymentLinkMobile = useCallback(async () => {
    await Share.share({
      message: selectedPaymentLink!.payment_link_url,
    });
  }, [selectedPaymentLink]);

  const onSharePaymentLinkWeb = useCallback(async () => {
    Clipboard.setStringAsync(selectedPaymentLink!.payment_link_url);
    showToast({
      content: i18n.t('payment_links.link_copied'),
    });
  }, [selectedPaymentLink, showToast]);

  return paymentLinks.length === 0 ? (
    <EmptyState
      description={i18n.t('payment_links.empty_message')}
      icon={<ChainLinkIcon stroke="none" />}
      title={i18n.t('payment_links.empty_title')}
    />
  ) : (
    <View style={styles.base}>
      <PaymentLinksList
        listSeparatorSpacing={contentSpacing}
        paymentLinks={paymentLinks}
        refreshControl={refreshControl}
        renderListItem={renderListItem}
        selectedPaymentLink={selectedPaymentLink}
      />
      {selectedPaymentLink ? (
        <View
          style={[
            styles.buttonContainer,
            { paddingHorizontal: theme.sk.spacing[contentSpacing] },
          ]}
        >
          <Button
            accessibilityLabel={i18n.t('payment_links.copy_payment_link')}
            onPress={
              Platform.OS === 'web'
                ? onSharePaymentLinkWeb
                : onSharePaymentLinkMobile
            }
            trackingData={{ name: 'payment_links.copy_payment_link_button' }}
          >
            {i18n.t(
              Platform.OS === 'web'
                ? 'payment_links.copy_payment_link'
                : 'payment_links.share_payment_link',
            )}
          </Button>
        </View>
      ) : null}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    flex: 1,
  },
  buttonContainer: {
    paddingVertical: theme.sk.spacing.spaceMedium,
  },
}));
